// Meta (Facebook) conversion tracking for the carrier signup funnel.
//
// Two events are fired today, at two different moments:
//
//   Lead                 — the carrier submits screen one and moves to screen two
//                          (business_info -> personal_info, Brittany's 00 -> 01). Fired from
//                          BusinessInfo.tsx. This is the early, higher-volume signal Meta
//                          optimizes on.
//   CompleteRegistration — the carrier finishes the collected application and moves to
//                          pre-active (the Assets step advances to `pending_review`, which
//                          triggers the backend `handleGoingToPreActiveStatus`). Fired from
//                          BusinessAssets.tsx. This is the late signal, kept for reporting and
//                          conversion value.
//
// CompleteRegistration was deliberately NOT repointed to 00 -> 01. Meta trains on an event's
// history and has no notion of an event changing meaning, so repointing would blend ten months
// of "finished the application" into one name with "started it", and would leave no late-funnel
// event to report value on.

// TypeScript declaration for the Meta Pixel global.
declare global {
  interface Window {
    fbq?: (
      command: string,
      eventName: string,
      params?: Record<string, any>,
      options?: Record<string, any>
    ) => void;
  }
}

/**
 * Helper function to get a cookie value by name.
 */
const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

/**
 * The Meta events we fire. The backend's Joi schema also accepts `SubmitApplication`
 * (services/facebook/src/apiTypes.ts) but nothing fires it yet, so it is left out here rather
 * than carried as an unused payload entry.
 */
export type MetaEventName = 'CompleteRegistration' | 'Lead';

/**
 * Per-event `custom_data` for the browser pixel.
 *
 * These are NOT part of what Meta matches on — deduplication keys off event_name and event_id
 * only. CompleteRegistration's content_name has always differed from the one the backend
 * workflow sends ('Carrier Signup Complete') and that mismatch is harmless; it is left exactly
 * as it was rather than churned, since changing it would alter ten months of reporting labels
 * for no functional gain. Lead's is aligned with the backend from the start.
 */
const META_EVENT_CONTENT: Record<
  MetaEventName,
  { content_name: string; status: string }
> = {
  CompleteRegistration: {
    content_name: 'Carrier Signup - Application Completed',
    status: 'application_completed',
  },
  Lead: {
    content_name: 'Carrier Application Started',
    status: 'application_started',
  },
};

/**
 * Builds the id that deduplicates the browser pixel event against the Conversions API event.
 *
 * This is deliberately NOT sent to the backend. The trackConversionMetaEvent workflow derives
 * the identical string from the same inputs — see Step 6 in
 * tach-architecture/temporal/src/workflows/adWorkflows/trackConversionMetaEvent.ts. The two
 * must stay byte-identical; if you change the format here, change it there in the same PR.
 * Nothing enforces this across repos, so both sides pin the exact string in a test.
 *
 * Stable by design, with no timestamp in it. Both call sites can fire more than once for one
 * application — BusinessAssets on every successful Assets submit, BusinessInfo if the carrier
 * goes back to screen one and re-submits — so a per-event unique id would restore the
 * double-counting this format exists to prevent.
 */
export const buildMetaEventId = (
  eventName: MetaEventName,
  applicationId: string
): string => `${eventName.toLowerCase()}_${applicationId}`;

/**
 * Fires a Meta Pixel event (client-side), paired to its Conversions API twin by event_id.
 */
export const trackMetaPixelEvent = ({
  eventName,
  applicationId,
}: {
  eventName: MetaEventName;
  applicationId: string;
}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    const { content_name, status } = META_EVENT_CONTENT[eventName];
    window.fbq(
      'track',
      eventName,
      {
        content_name,
        content_category: 'Signup',
        content_ids: [applicationId],
        status,
      },
      // Without this 4th argument Meta cannot pair this with the Conversions API event, and
      // the signup is reported as two conversions rather than one.
      { eventID: buildMetaEventId(eventName, applicationId) }
    );
  } else if (process.env.REACT_APP_ENV === 'prod') {
    // Only a problem in prod. Outside it the pixel is deliberately not loaded
    // (see public/index.html), so a missing fbq is the expected state, not a fault.
    console.warn('Meta Pixel fbq not available for conversion tracking');
  }
};

/**
 * Tracks CompleteRegistration in Meta Pixel (client-side) when the application is completed and the
 * customer moves to pre-active.
 */
export const trackMetaCompleteRegistration = ({
  applicationId,
}: {
  applicationId: string;
}) => trackMetaPixelEvent({ eventName: 'CompleteRegistration', applicationId });

/**
 * Tracks Lead in Meta Pixel (client-side) when the carrier clears screen one of the application.
 */
export const trackMetaLead = ({ applicationId }: { applicationId: string }) =>
  trackMetaPixelEvent({ eventName: 'Lead', applicationId });

/**
 * Tracks a conversion via Meta Conversions API (server-side).
 *
 * The server-side tracking:
 * - Cannot be blocked by ad blockers or privacy extensions
 * - Automatically hashes all PII (email, phone, DOB, address)
 * - Provides better attribution accuracy
 * - Deduplicates against the client-side pixel via the shared event_id both sides derive
 *   (fbp/fbc are user-matching signals, not the dedup key)
 *
 * Production only. This path never touches window.fbq, so gating the pixel in
 * index.html does not stop it — staging would still POST here, and the workflow
 * reads /staging/meta/pixelId, which holds the same id as /prod/meta/pixelId.
 * Internal test signups would land in the live pixel, feeding conversion
 * optimization and lookalike seeds.
 */
export const trackMetaConversionServerSide = async ({
  applicationId,
  eventName,
  callProtectedApi,
}: {
  applicationId: string;
  eventName: MetaEventName;
  callProtectedApi: any;
}) => {
  if (process.env.REACT_APP_ENV !== 'prod') return;

  try {
    // Get Facebook browser cookies for attribution
    const fbp = getCookie('_fbp'); // Facebook browser pixel cookie
    const fbc = getCookie('_fbc'); // Facebook click ID cookie
    // Call server-side Meta Conversions API via Lambda
    await callProtectedApi(
      '/facebook/conversion',
      'post',
      {
        application_id: applicationId,
        event_name: eventName,
        event_source_url: 'https://app.tachusa.com/signup',
        fbp,
        fbc,
        // ip_address and user_agent will be automatically extracted from headers by Lambda
      },
      'Failed to track Meta conversion'
    );
  } catch (error) {
    console.error('Error tracking Meta server-side conversion:', error);
    // Don't throw - we don't want tracking errors to break the user flow
  }
};
